import { createSlice } from "@reduxjs/toolkit";
import { mesaKicked } from "src/redux/actions/mesaKick";
import {
  applyLeaseEvent,
  presenceFromSnapshot,
  pruneExpired,
} from "src/utils/mesaLeaseCore";

// Presencia de mesas (quién está dentro de cada mesa) + leases propios.
// Fase 1 TPV (2026-09-29). La lógica vive en utils/mesaLeaseCore (pura,
// testeada); la orquestación (POST/heartbeat/release) en
// utils/mesaLeaseManager.
//
//   byMesaId  — presencia de TODO el restaurante (snapshot + eventos hub).
//   myLeases  — { mesaId → { leaseId, mode } } de ESTE dispositivo.
//   revoked   — { mesaId, holder, ts } cuando me han quitado una mesa
//               (lo consume el bridge: sale al listado + snackbar).
//   chargingPrompt — { mesaId, holder, mode } tras 409 LEASE_CHARGING
//               (diálogo "¿Forzar?").

const initialState = {
  byMesaId: {},
  myLeases: {},
  revoked: null,
  // Mi lease caducó sin que nadie me quitara la mesa → re-adquirir en silencio.
  reacquireRequest: null,
  // Igual que `revoked` pero NO se limpia al gestionarlo: el PaymentDialog
  // lo compara por `ts` para cerrarse si me quitan la mesa mientras cobro.
  lastRevoked: null,
  // { mesaId, ts } cuando me quitan la mesa que tenía ABIERTA (ver
  // redux/actions/mesaKick): useOnMesaKicked cierra diálogos locales.
  kicked: null,
  chargingPrompt: null,
};

const slice = createSlice({
  name: "mesaLeases",
  initialState,
  extraReducers: (builder) => {
    builder.addCase(mesaKicked, (state, action) => {
      state.kicked = action.payload;
    });
  },
  reducers: {
    leasesSnapshotReceived(state, action) {
      state.byMesaId = presenceFromSnapshot(action.payload);
    },
    leaseEventReceived(state, action) {
      const { evt, myDeviceId } = action.payload || {};
      const { byMesaId, takeover, reacquire } = applyLeaseEvent(
        state.byMesaId,
        state.myLeases,
        evt,
        myDeviceId,
      );
      state.byMesaId = byMesaId;
      if (takeover) {
        delete state.myLeases[takeover.mesaId];
        state.revoked = { ...takeover, ts: Date.now() };
        state.lastRevoked = state.revoked;
      }
      if (reacquire) {
        // El manager decide: re-adquirir en silencio o, si otro la tiene, takeover.
        delete state.myLeases[reacquire];
        state.reacquireRequest = { mesaId: reacquire, ts: Date.now() };
      }
    },
    reacquireHandled(state) {
      state.reacquireRequest = null;
    },
    myLeaseSet(state, action) {
      const { mesaId, leaseId, mode, holder, expiresAt } = action.payload || {};
      if (!mesaId || !leaseId) return;
      state.myLeases[mesaId] = { leaseId, mode };
      // Presencia local inmediata (el evento del hub llegará después).
      state.byMesaId[mesaId] = { leaseId, mesaId, mode, expiresAt: expiresAt ?? null, holder: holder || null };
    },
    myLeaseRemoved(state, action) {
      const mesaId = action.payload;
      const mine = state.myLeases[mesaId];
      delete state.myLeases[mesaId];
      if (mine && state.byMesaId[mesaId]?.leaseId === mine.leaseId) {
        delete state.byMesaId[mesaId];
      }
    },
    leaseRevoked(state, action) {
      const { mesaId, holder } = action.payload || {};
      if (!mesaId) return;
      delete state.myLeases[mesaId];
      state.revoked = { mesaId, holder: holder || null, ts: Date.now() };
      state.lastRevoked = state.revoked;
    },
    revokedHandled(state) {
      state.revoked = null;
    },
    setChargingPrompt(state, action) {
      state.chargingPrompt = action.payload || null;
    },
    pruneExpiredLeases(state) {
      const next = pruneExpired(state.byMesaId);
      if (next !== state.byMesaId) state.byMesaId = next;
    },
    clearMesaLeases() {
      return initialState;
    },
  },
});

export default slice.reducer;

export const {
  leasesSnapshotReceived,
  leaseEventReceived,
  myLeaseSet,
  myLeaseRemoved,
  leaseRevoked,
  reacquireHandled,
  revokedHandled,
  setChargingPrompt,
  pruneExpiredLeases,
  clearMesaLeases,
} = slice.actions;

// Selector estable por mesa: devuelve la MISMA referencia mientras esa
// mesa no cambie → el badge de cada tile solo repinta cuando cambia su
// propia presencia (no la de las demás).
export const selectMesaLease = (mesaId) => (state) =>
  state.mesaLeases?.byMesaId?.[mesaId] || null;
